import Spinner from "./spinner";

/**
 * Shared building blocks. Every page composes these, so the visual language —
 * radii, elevation, spacing rhythm, state colours — is defined once here and
 * changing it restyles the whole app.
 */

export function Page({ children, className, wide }: any) {
  return (
    <div className={`rise mx-auto w-full ${wide ? 'max-w-7xl' : 'max-w-5xl'} p-4 sm:p-6 lg:p-8 ${className ?? ''}`}>
      {children}
    </div>
  )
}

export function Card({ children, className, padded = true }: any) {
  return (
    <div className={`rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-sm shadow-slate-900/[0.03] ${padded ? 'p-4 sm:p-5' : ''} ${className ?? ''}`}>
      {children}
    </div>
  )
}

export function SectionTitle({ children, icon: Icon, actions, className }: any) {
  return (
    <div className={`flex flex-row items-center gap-3 ${className ?? ''}`}>
      <div className="flex grow flex-row items-center gap-2.5 min-w-0">
        {Icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Icon size={16} />
          </span>
        )}
        <span className="truncate text-base font-bold tracking-tight text-slate-900">{children}</span>
      </div>
      {actions}
    </div>
  )
}

/** Label/value row that stacks under its label on small screens. */
export function Field({ label, children, className }: any) {
  return (
    <div className={`flex flex-col gap-0.5 border-b border-slate-100 py-2.5 last:border-0 sm:flex-row sm:items-baseline sm:gap-4 ${className ?? ''}`}>
      <div className="shrink-0 sm:w-44 text-[11px] uppercase tracking-wide text-slate-400 sm:text-xs sm:normal-case sm:tracking-normal">{label}</div>
      <div className="grow min-w-0 break-words text-sm font-semibold text-slate-800">{children}</div>
    </div>
  )
}

export function Loading({ label }: any) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <Spinner className="w-9 h-9 text-blue-600 fill-slate-200" />
      {label && <div className="text-xs font-medium text-slate-400">{label}</div>}
    </div>
  )
}

export function ErrorState({ error }: any) {
  if (!error) return null;
  return (
    <div className="mx-auto my-8 max-w-lg rounded-2xl bg-red-50 px-5 py-4 text-center text-sm font-medium text-red-700 ring-1 ring-red-100">
      <div dangerouslySetInnerHTML={{ __html: error }} />
    </div>
  )
}

export function EmptyState({ icon: Icon, title, message, action }: any) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-white px-6 py-16 text-center ring-1 ring-slate-200/70">
      {Icon && (
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-300 ring-1 ring-slate-100">
          <Icon size={26} />
        </span>
      )}
      <div className="text-base font-bold text-slate-800">{title}</div>
      {message && <div className="max-w-sm text-sm text-slate-500">{message}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

const pillTones: any = {
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
  red: 'bg-red-50 text-red-700 ring-red-200/70',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200/70',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200/70',
  gray: 'bg-slate-100 text-slate-600 ring-slate-200/70',
};

export function Pill({ tone = 'gray', children, icon: Icon, className }: any) {
  return (
    <span className={`inline-flex shrink-0 flex-row items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${pillTones[tone] ?? pillTones.gray} ${className ?? ''}`}>
      {Icon && <Icon size={11} />}
      {children}
    </span>
  )
}

const btnTones: any = {
  primary: 'bg-gradient-to-b from-blue-500 to-blue-600 text-white shadow-blue-600/20 hover:from-blue-600 hover:to-blue-700',
  green: 'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-emerald-600/20 hover:from-emerald-600 hover:to-emerald-700',
  danger: 'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-red-600/20 hover:from-red-600 hover:to-red-700',
  ghost: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300',
};

/** min-h keeps every button a comfortable tap target on phones. */
export function Button({ tone = 'primary', className, children, loading, ...rest }: any) {
  return (
    <button
      {...rest}
      className={`inline-flex min-h-[40px] shrink-0 flex-row items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow-lg transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:ring-0 ${btnTones[tone] ?? btnTones.primary} ${className ?? ''}`}>
      {loading && <Spinner className="w-4 h-4" />}
      {children}
    </button>
  )
}

/** Tables scroll inside their own card so the page never scrolls sideways. */
export function TableWrap({ children, className }: any) {
  return (
    <div className={`thin-scroll overflow-x-auto rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-sm shadow-slate-900/[0.03] ${className ?? ''}`}>
      {children}
    </div>
  )
}
